// features/admin/deliveryAgent.ts - COMPLETE WITH APPROVAL + EXPIRY + SAFETY FIELDS
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { adminrefreshToken } from '../../utils/adminRefreshToken.ts';

export interface DeliveryAgent {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  contact: string;
  password?: string;
  address: string;
  nationality: string;
  id_no?: string;
  id_front?: string;
  id_back?: string;
  vehicle_reg_no?: string;
  vehicle_reg_front?: string;
  vehicle_reg_back?: string;
  licence_no?: string;
  licence_front?: string;
  licence_back?: string;
  licence_status?: number;
  licence_reject_reason?: string;
  passport_no?: string;
  passport_front?: string;
  passport_back?: string;
  passport_status?: number;
  passport_reject_reason?: string;
  id_status?: number;
  id_reject_reason?: string;
  vehicle_reg_status?: number;
  vreg_reject_reason?: string;
  status?: number;
  created_at?: string;
  updated_at?: string;

  // expiry fields from backend
  id_validity?: string | null;
  passport_validity?: string | null;
  licence_validity?: string | null;
  vehicle_reg_validity?: string | null;

  // safety / profile fields from backend
  safety_name?: string;
  safety_contact?: string;
  profile_photo?: string;
  active?: number;
  status_availabilty?: number;
}

export interface DeliveryAgentApiResponse<T> {
  error: boolean;
  message: string;
  data?: T;
  result?: T;
}

export interface AddDeliveryAgentRequest {
  first_name: string;
  last_name: string;
  email: string;
  contact: string;
  password: string;
  address: string;
  nationality: string;
  licence_no?: string;
  licence_front?: File | string;
  licence_back?: File | string;
  licence_status?: number;
  licence_reject_reason?: string;
  vehicle_reg_no?: string;
  vehicle_reg_front?: File | string;
  vehicle_reg_back?: File | string;
  vehicle_reg_status?: number;
  vreg_reject_reason?: string;
  id_no?: string;
  id_front?: File | string;
  id_back?: File | string;
  id_status?: number;
  id_reject_reason?: string;
  passport_no?: string;
  passport_front?: File | string;
  passport_back?: File | string;
  passport_status?: number;
  passport_reject_reason?: string;

  // expiry fields
  id_validity?: string | null;
  passport_validity?: string | null;
  licence_validity?: string | null;
  vehicle_reg_validity?: string | null;

  // safety / profile fields
  safety_name?: string;
  safety_contact?: string;
  profile_photo?: File | string;
  active?: number;
  status_availabilty?: number;
}

export interface UpdateDeliveryAgentRequest {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  contact?: string;
  password?: string;
  address?: string;
  nationality?: string;
  licence_no?: string;
  licence_front?: File | string;
  licence_back?: File | string;
  licence_status?: number;
  licence_reject_reason?: string;
  vehicle_reg_no?: string;
  vehicle_reg_front?: File | string;
  vehicle_reg_back?: File | string;
  vehicle_reg_status?: number;
  vreg_reject_reason?: string;
  id_no?: string;
  id_front?: File | string;
  id_back?: File | string;
  id_status?: number;
  id_reject_reason?: string;
  passport_no?: string;
  passport_front?: File | string;
  passport_back?: File | string;
  passport_status?: number;
  passport_reject_reason?: string;

  // expiry fields
  id_validity?: string | null;
  passport_validity?: string | null;
  licence_validity?: string | null;
  vehicle_reg_validity?: string | null;

  // safety / profile fields
  safety_name?: string;
  safety_contact?: string;
  profile_photo?: File | string;
  active?: number;
  status_availabilty?: number;
}

export interface DeleteDeliveryAgentRequest {
  id: number;
}

export interface ApproveDeliveryAgentRequest {
  id: number;
  licence_status?: number;
  id_status?: number;
  passport_status?: number;
  vehicle_reg_status?: number;
  licence_reject_reason?: string;
  id_reject_reason?: string;
  passport_reject_reason?: string;
  vreg_reject_reason?: string;
}

// Custom base query with enhanced error handling
const baseQueryWithReauth = fetchBaseQuery({
  baseUrl: 'https://fftestservice.sarsetech.com',
  prepareHeaders: async (headers) => {
    console.log('=== PREPARE HEADERS ===');

    headers.set(
      'authkey',
      '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3'
    );

    try {
      await adminrefreshToken();
      console.log('Token refresh successful');
    } catch (err) {
      console.error('Token refresh failed:', err);
    }

    const token = localStorage.getItem('admin_token');
    console.log('Token exists:', !!token);
    console.log(
      'Token value:',
      token ? `${token.substring(0, 20)}...` : 'null'
    );

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  },
});

export const deliveryAgentApi = createApi({
  reducerPath: 'deliveryAgentApi',
  baseQuery: async (args, api, extraOptions) => {
    console.log('=== BASE QUERY ===');
    console.log('URL:', (args as any).url);
    console.log('Method:', (args as any).method);

    const result = await baseQueryWithReauth(args, api, extraOptions);

    console.log('=== RESPONSE ===');
    console.log('Result:', result);
    console.log('Data:', result.data);
    console.log('Error:', result.error);
    console.log('Meta:', result.meta);

    return result;
  },
  tagTypes: ['DeliveryAgent'],
  endpoints: (build) => ({
    listDeliveryAgents: build.query<
      DeliveryAgentApiResponse<DeliveryAgent[]>,
      void
    >({
      query: () => {
        const body = new URLSearchParams();
        return {
          url: 'product/listalldeliveryagent',
          method: 'POST',
          body: body,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        };
      },
      providesTags: ['DeliveryAgent'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || '',
        data: response.data || [],
      }),
    }),

    addDeliveryAgent: build.mutation<
      DeliveryAgentApiResponse<any>,
      AddDeliveryAgentRequest
    >({
      query: (data) => {
        const formData = new FormData();

        console.log('=== ADD DELIVERY AGENT ===');
        console.log('Input data:', data);

        // Required fields
        formData.append('first_name', data.first_name);
        formData.append('last_name', data.last_name);
        formData.append('email', data.email);
        formData.append('contact', data.contact);
        formData.append('password', data.password);
        formData.append('address', data.address);
        formData.append('nationality', data.nationality);

        // Optional fields
        if (data.licence_no) formData.append('licence_no', data.licence_no);
        if (data.vehicle_reg_no)
          formData.append('vehicle_reg_no', data.vehicle_reg_no);
        if (data.id_no) formData.append('id_no', data.id_no);
        if (data.passport_no) formData.append('passport_no', data.passport_no);

        // Safety / profile
        if (data.safety_name) formData.append('safety_name', data.safety_name);
        if (data.safety_contact)
          formData.append('safety_contact', data.safety_contact);
        if (data.status_availabilty !== undefined)
          formData.append(
            'status_availabilty',
            data.status_availabilty.toString()
          );
        if (data.active !== undefined)
          formData.append('active', data.active.toString());

        // Status fields
        if (data.licence_status !== undefined)
          formData.append('licence_status', data.licence_status.toString());
        if (data.licence_reject_reason)
          formData.append(
            'licence_reject_reason',
            data.licence_reject_reason
          );
        if (data.id_status !== undefined)
          formData.append('id_status', data.id_status.toString());
        if (data.id_reject_reason)
          formData.append('id_reject_reason', data.id_reject_reason);
        if (data.passport_status !== undefined)
          formData.append('passport_status', data.passport_status.toString());
        if (data.passport_reject_reason)
          formData.append(
            'passport_reject_reason',
            data.passport_reject_reason
          );
        if (data.vehicle_reg_status !== undefined)
          formData.append(
            'vehicle_reg_status',
            data.vehicle_reg_status.toString()
          );
        if (data.vreg_reject_reason)
          formData.append('vreg_reject_reason', data.vreg_reject_reason);

        // Expiry date fields
        if (data.id_validity) formData.append('id_validity', data.id_validity);
        if (data.passport_validity)
          formData.append('passport_validity', data.passport_validity);
        if (data.licence_validity)
          formData.append('licence_validity', data.licence_validity);
        if (data.vehicle_reg_validity)
          formData.append(
            'vehicle_reg_validity',
            data.vehicle_reg_validity
          );

        // Files - Only append if they are actual File objects
        if (data.id_front instanceof File) {
          formData.append('id_front', data.id_front, data.id_front.name);
          console.log('Added id_front:', data.id_front.name);
        }
        if (data.id_back instanceof File) {
          formData.append('id_back', data.id_back, data.id_back.name);
          console.log('Added id_back:', data.id_back.name);
        }
        if (data.vehicle_reg_front instanceof File) {
          formData.append(
            'vehicle_reg_front',
            data.vehicle_reg_front,
            data.vehicle_reg_front.name
          );
          console.log('Added vehicle_reg_front:', data.vehicle_reg_front.name);
        }
        if (data.vehicle_reg_back instanceof File) {
          formData.append(
            'vehicle_reg_back',
            data.vehicle_reg_back,
            data.vehicle_reg_back.name
          );
          console.log('Added vehicle_reg_back:', data.vehicle_reg_back.name);
        }
        if (data.licence_front instanceof File) {
          formData.append(
            'licence_front',
            data.licence_front,
            data.licence_front.name
          );
          console.log('Added licence_front:', data.licence_front.name);
        }
        if (data.licence_back instanceof File) {
          formData.append(
            'licence_back',
            data.licence_back,
            data.licence_back.name
          );
          console.log('Added licence_back:', data.licence_back.name);
        }
        if (data.passport_front instanceof File) {
          formData.append(
            'passport_front',
            data.passport_front,
            data.passport_front.name
          );
          console.log('Added passport_front:', data.passport_front.name);
        }
        if (data.passport_back instanceof File) {
          formData.append(
            'passport_back',
            data.passport_back,
            data.passport_back.name
          );
          console.log('Added passport_back:', data.passport_back.name);
        }
        if (data.profile_photo instanceof File) {
          formData.append(
            'profile_photo',
            data.profile_photo,
            data.profile_photo.name
          );
          console.log('Added profile_photo:', data.profile_photo.name);
        }

        // Log all FormData entries
        console.log('FormData entries:');
        for (let [key, value] of formData.entries()) {
          console.log(
            `${key}:`,
            value instanceof File ? `File: ${value.name}` : value
          );
        }

        return {
          url: 'product/adddeliveryagent',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['DeliveryAgent'],
    }),

    updateDeliveryAgent: build.mutation<
      DeliveryAgentApiResponse<any>,
      UpdateDeliveryAgentRequest
    >({
      query: (data) => {
        const hasFiles =
          data.id_front instanceof File ||
          data.id_back instanceof File ||
          data.vehicle_reg_front instanceof File ||
          data.vehicle_reg_back instanceof File ||
          data.licence_front instanceof File ||
          data.licence_back instanceof File ||
          data.passport_front instanceof File ||
          data.passport_back instanceof File ||
          data.profile_photo instanceof File;

        console.log('=== UPDATE DELIVERY AGENT ===');
        console.log('Agent ID:', data.id);
        console.log('Has files:', hasFiles);

        if (hasFiles) {
          const formData = new FormData();

          formData.append('id', data.id.toString());

          if (data.first_name) formData.append('first_name', data.first_name);
          if (data.last_name) formData.append('last_name', data.last_name);
          if (data.email) formData.append('email', data.email);
          if (data.contact) formData.append('contact', data.contact);
          if (data.password) formData.append('password', data.password);
          if (data.address) formData.append('address', data.address);
          if (data.nationality)
            formData.append('nationality', data.nationality);
          if (data.passport_no)
            formData.append('passport_no', data.passport_no);
          if (data.licence_no) formData.append('licence_no', data.licence_no);
          if (data.vehicle_reg_no)
            formData.append('vehicle_reg_no', data.vehicle_reg_no);
          if (data.id_no) formData.append('id_no', data.id_no);

          if (data.safety_name)
            formData.append('safety_name', data.safety_name);
          if (data.safety_contact)
            formData.append('safety_contact', data.safety_contact);
          if (data.status_availabilty !== undefined)
            formData.append(
              'status_availabilty',
              data.status_availabilty.toString()
            );
          if (data.active !== undefined)
            formData.append('active', data.active.toString());

          // Status fields
          if (data.licence_status !== undefined)
            formData.append('licence_status', data.licence_status.toString());
          if (data.licence_reject_reason)
            formData.append(
              'licence_reject_reason',
              data.licence_reject_reason
            );
          if (data.id_status !== undefined)
            formData.append('id_status', data.id_status.toString());
          if (data.id_reject_reason)
            formData.append('id_reject_reason', data.id_reject_reason);
          if (data.passport_status !== undefined)
            formData.append(
              'passport_status',
              data.passport_status.toString()
            );
          if (data.passport_reject_reason)
            formData.append(
              'passport_reject_reason',
              data.passport_reject_reason
            );
          if (data.vehicle_reg_status !== undefined)
            formData.append(
              'vehicle_reg_status',
              data.vehicle_reg_status.toString()
            );
          if (data.vreg_reject_reason)
            formData.append('vreg_reject_reason', data.vreg_reject_reason);

          // Expiry fields
          if (data.id_validity)
            formData.append('id_validity', data.id_validity);
          if (data.passport_validity)
            formData.append('passport_validity', data.passport_validity);
          if (data.licence_validity)
            formData.append('licence_validity', data.licence_validity);
          if (data.vehicle_reg_validity)
            formData.append(
              'vehicle_reg_validity',
              data.vehicle_reg_validity
            );

          // Files
          if (data.id_front instanceof File)
            formData.append('id_front', data.id_front);
          if (data.id_back instanceof File)
            formData.append('id_back', data.id_back);
          if (data.vehicle_reg_front instanceof File)
            formData.append('vehicle_reg_front', data.vehicle_reg_front);
          if (data.vehicle_reg_back instanceof File)
            formData.append('vehicle_reg_back', data.vehicle_reg_back);
          if (data.licence_front instanceof File)
            formData.append('licence_front', data.licence_front);
          if (data.licence_back instanceof File)
            formData.append('licence_back', data.licence_back);
          if (data.passport_front instanceof File)
            formData.append('passport_front', data.passport_front);
          if (data.passport_back instanceof File)
            formData.append('passport_back', data.passport_back);
          if (data.profile_photo instanceof File)
            formData.append('profile_photo', data.profile_photo);

          console.log('Using FormData (multipart/form-data)');
          formData.forEach((value, key) => {
            console.log(
              `${key}:`,
              value instanceof File ? value.name : value
            );
          });

          return {
            url: 'product/updatedeliveryagent',
            method: 'POST',
            body: formData,
          };
        } else {
          const body = new URLSearchParams();

          body.append('id', data.id.toString());

          if (data.first_name) body.append('first_name', data.first_name);
          if (data.last_name) body.append('last_name', data.last_name);
          if (data.email) body.append('email', data.email);
          if (data.contact) body.append('contact', data.contact);
          if (data.password) body.append('password', data.password);
          if (data.address) body.append('address', data.address);
          if (data.nationality) body.append('nationality', data.nationality);
          if (data.passport_no) body.append('passport_no', data.passport_no);
          if (data.licence_no) body.append('licence_no', data.licence_no);
          if (data.vehicle_reg_no)
            body.append('vehicle_reg_no', data.vehicle_reg_no);
          if (data.id_no) body.append('id_no', data.id_no);

          if (data.safety_name) body.append('safety_name', data.safety_name);
          if (data.safety_contact)
            body.append('safety_contact', data.safety_contact);
          if (data.status_availabilty !== undefined)
            body.append(
              'status_availabilty',
              data.status_availabilty.toString()
            );
          if (data.active !== undefined)
            body.append('active', data.active.toString());

          // Status fields
          if (data.licence_status !== undefined)
            body.append('licence_status', data.licence_status.toString());
          if (data.licence_reject_reason)
            body.append('licence_reject_reason', data.licence_reject_reason);
          if (data.id_status !== undefined)
            body.append('id_status', data.id_status.toString());
          if (data.id_reject_reason)
            body.append('id_reject_reason', data.id_reject_reason);
          if (data.passport_status !== undefined)
            body.append('passport_status', data.passport_status.toString());
          if (data.passport_reject_reason)
            body.append(
              'passport_reject_reason',
              data.passport_reject_reason
            );
          if (data.vehicle_reg_status !== undefined)
            body.append(
              'vehicle_reg_status',
              data.vehicle_reg_status.toString()
            );
          if (data.vreg_reject_reason)
            body.append('vreg_reject_reason', data.vreg_reject_reason);

          // Expiry fields (IMPORTANT: now sent even when no files)
          if (data.id_validity)
            body.append('id_validity', data.id_validity);
          if (data.passport_validity)
            body.append('passport_validity', data.passport_validity);
          if (data.licence_validity)
            body.append('licence_validity', data.licence_validity);
          if (data.vehicle_reg_validity)
            body.append(
              'vehicle_reg_validity',
              data.vehicle_reg_validity
            );

          console.log('Using URLSearchParams (x-www-form-urlencoded)');
          console.log('Body:', body.toString());

          return {
            url: 'product/updatedeliveryagent',
            method: 'POST',
            body: body,
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          };
        }
      },
      invalidatesTags: ['DeliveryAgent'],
    }),

    deleteDeliveryAgent: build.mutation<
      DeliveryAgentApiResponse<any>,
      DeleteDeliveryAgentRequest
    >({
      queryFn: async ({ id }, _queryApi, _extraOptions, baseQuery) => {
        console.log('=== DELETE QUERY FN START ===');
        console.log('Deleting agent ID:', id);

        const token = localStorage.getItem('admin_token');
        console.log('Token present:', !!token);

        const body = new URLSearchParams();
        body.append('id', id.toString());

        console.log('Body contents (x-www-form-urlencoded):');
        console.log('  id:', id.toString());
        console.log('Body string:', body.toString());

        try {
          const result: any = await baseQuery({
            url: 'product/deliveryagentdelete',
            method: 'POST',
            body: body,
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          });

          console.log('=== RAW RESULT ===');
          console.log('Full result:', JSON.stringify(result, null, 2));
          console.log('Result.data:', result.data);
          console.log('Result.error:', result.error);
          console.log('Result.meta:', result.meta);

          if (result.data) {
            const responseData = result.data as any;
            console.log('Response data structure:', {
              hasError: 'error' in responseData,
              errorValue: responseData.error,
              hasMessage: 'message' in responseData,
              messageValue: responseData.message,
            });

            if (
              responseData.error === false ||
              responseData.message?.toLowerCase().includes('success')
            ) {
              console.log('DELETE SUCCESS');
              return {
                data: {
                  error: false,
                  message:
                    responseData.message ||
                    'Delivery agent deleted successfully',
                  data: responseData.data || null,
                },
              };
            } else if (responseData.error === true) {
              console.log('DELETE FAILED (API returned error: true)');
              return {
                error: {
                  status: 'CUSTOM_ERROR',
                  data: responseData,
                },
              };
            }
          }

          if (result.error) {
            console.log('DELETE ERROR from baseQuery');
            console.log('Error details:', result.error);
            return {
              error: result.error,
            };
          }

          console.log('UNEXPECTED RESPONSE FORMAT');
          return {
            data: {
              error: false,
              message: 'Operation completed',
              data: result.data,
            },
          };
        } catch (error) {
          console.error('=== EXCEPTION IN DELETE ===');
          console.error('Exception:', error);
          return {
            error: {
              status: 'FETCH_ERROR',
              error: String(error),
            },
          };
        }
      },
      invalidatesTags: ['DeliveryAgent'],
    }),

    approveDeliveryAgent: build.mutation<
      DeliveryAgentApiResponse<any>,
      ApproveDeliveryAgentRequest
    >({
      query: (data) => {
        const body = new URLSearchParams();

        console.log('=== APPROVE DELIVERY AGENT ===');
        console.log('Agent ID:', data.id);
        console.log('Approval data:', data);

        body.append('id', data.id.toString());

        if (data.licence_status !== undefined)
          body.append('licence_status', data.licence_status.toString());
        if (data.id_status !== undefined)
          body.append('id_status', data.id_status.toString());
        if (data.passport_status !== undefined)
          body.append('passport_status', data.passport_status.toString());
        if (data.vehicle_reg_status !== undefined)
          body.append(
            'vehicle_reg_status',
            data.vehicle_reg_status.toString()
          );

        if (data.licence_reject_reason)
          body.append('licence_reject_reason', data.licence_reject_reason);
        if (data.id_reject_reason)
          body.append('id_reject_reason', data.id_reject_reason);
        if (data.passport_reject_reason)
          body.append(
            'passport_reject_reason',
            data.passport_reject_reason
          );
        if (data.vreg_reject_reason)
          body.append('vreg_reject_reason', data.vreg_reject_reason);

        console.log('Body contents:', body.toString());

        return {
          url: 'product/approvedeliveryagent',
          method: 'POST',
          body: body,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        };
      },
      invalidatesTags: ['DeliveryAgent'],
    }),
  }),
});

export const {
  useListDeliveryAgentsQuery,
  useAddDeliveryAgentMutation,
  useUpdateDeliveryAgentMutation,
  useDeleteDeliveryAgentMutation,
  useApproveDeliveryAgentMutation,
} = deliveryAgentApi;
